import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const { cart, removeFromCart, cartTotal } = useCart();
  const navigate = useNavigate();

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-testid="empty-cart-message">
        <ShoppingBag className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-3xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Your cart is empty
        </h2>
        <p className="text-muted-foreground mb-6">Add some beautiful Bandhani pieces to your cart</p>
        <Link to="/products">
          <Button className="rounded-full px-8 py-4 uppercase tracking-widest text-xs">Shop Now</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-12" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
        Shopping Cart
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6" data-testid="cart-items-list">
          {cart.map((item) => (
            <div key={item.product_id} className="flex gap-6 border-b border-border pb-6" data-testid={`cart-item-${item.product_id}`}>
              <img 
                src={item.product?.image} 
                alt={item.product?.name} 
                className="w-32 h-32 object-cover"
              />
              <div className="flex-1">
                <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {item.product?.name}
                </h3>
                <p className="text-sm text-muted-foreground mb-2">{item.product?.category}</p>
                <p className="text-lg font-light mb-2">₹{item.product?.price?.toLocaleString('en-IN')}</p>
                <p className="text-sm">Quantity: {item.quantity}</p>
              </div>
              <div className="flex flex-col justify-between items-end">
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => handleRemove(item.product_id)}
                  data-testid={`remove-item-${item.product_id}`}
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
                <p className="text-xl font-light">₹{(item.product?.price * item.quantity).toLocaleString('en-IN')}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-secondary p-8 sticky top-24">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Order Summary
            </h3>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="border-t border-border pt-4 flex justify-between text-xl font-light">
                <span>Total</span>
                <span data-testid="cart-total">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/checkout')}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 uppercase tracking-widest text-sm font-medium"
              data-testid="proceed-to-checkout-button"
            >
              Proceed to Checkout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;